import { useCallback, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import { useLazyFetchUsersQuery } from '@api/Trinium/Endpoints/User/slice';
import { FormikAutocomplete } from '@forms/Components/AutocompleteInput';
import useDebouncer from '@hooks/useDebounce';

interface UserFieldProps {
  fieldName: string;
  className?: string;
}

export function UserField({ fieldName, className }: UserFieldProps) {
  const { getFieldMeta } = useFormikContext();
  const { value } = getFieldMeta<string>(fieldName);
  const [fetchUsers, { userNames, fetchingUsers }] = useLazyFetchUsersQuery({
    selectFromResult: ({ data, isFetching }) => ({
      /** Because we are using the URL Parameter, we can only support the string.
       * Using a complex object would require making an API call to get the User object
       * before being able to plug into the autocomplete input.
       */
      userNames: data ? data.map((user) => `(${user.code}) ${user.name}`) : value ? [value] : [],
      fetchingUsers: isFetching
    })
  });

  const [debouncedUserValue, setDebouncedUserValue] = useState<string | null>(null);
  useDebouncer(
    () => {
      if (debouncedUserValue && debouncedUserValue.length > 2) {
        void fetchUsers({
          contains: debouncedUserValue
        });
      }
    },
    [debouncedUserValue, fetchUsers],
    500
  );

  /**
   * Callback for the on change event upon keystroke
   */
  const onChangeExtension = useCallback(
    (value: string | null) => {
      setDebouncedUserValue(value);
    },
    [setDebouncedUserValue]
  );

  return (
    <Field
      name={fieldName}
      label='Created By'
      placeholder='Enter User Name'
      component={FormikAutocomplete}
      autoCompleteItems={userNames}
      isLoading={fetchingUsers}
      className={className}
      onChangeExtension={onChangeExtension}
    />
  );
}
